import api from './api';

export interface KsefSettings {
  authType: 'token' | 'certificate';
  token?: string;
  certificateFile?: string;
  certificatePassword?: string;
  contextNip?: string;
  environment?: 'demo' | 'production';
}

export interface KsefConnectionStatus {
  connected: boolean;
  lastCheck?: string;
  error?: string;
  message?: string;
  phase?: 'connected' | 'pending' | 'disconnected';
  referenceNumber?: string;
  status?: any;
}

export interface KsefInvoiceListItem {
  ksef_invoice_id: string;
  reference_number?: string | null;
  subject_type: 'subject1' | 'subject2' | 'subject3';
  invoice_number?: string | null;
  issue_date?: string | null;
  invoicing_date?: string | null;
  payment_due_date?: string | null;
  payment_method?: string | null;
  payment_term?: string | null;
  bank_account_number?: string | null;
  seller_name?: string | null;
  seller_tax_id?: string | null;
  buyer_name?: string | null;
  buyer_tax_id?: string | null;
  net_amount?: string | number | null;
  vat_amount?: string | number | null;
  gross_amount?: string | number | null;
  currency?: string | null;
  invoice_type?: string | null;
  status?: string | null;
  acquisition_timestamp?: string | null;
  pdf_drive_id?: string | null;
}

export interface KsefBankAccount {
  nr_rb: string;
  bank_name?: string | null;
  swift?: string | null;
  description?: string | null;
  currency?: string | null;
  is_primary?: number | boolean;
}

export interface KsefInvoiceTotals {
  net: number;
  vat: number;
  gross: number;
}

export interface KsefInvoiceDetails {
  invoice: any;
  items: any[];
  bankAccounts?: KsefBankAccount[];
  calcMode?: 'net' | 'gross';
  isNetto?: boolean;
  totalsFromItems?: KsefInvoiceTotals;
  totalsMatch?: { net: boolean; vat: boolean; gross: boolean };
}

// Pobranie ustawień KSEF
export async function getKsefSettings(): Promise<KsefSettings | null> {
  try {
    const { data } = await api.get('/ksef/settings');
    return data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// Zapisanie ustawień KSEF
export async function saveKsefSettings(settings: KsefSettings): Promise<KsefSettings> {
  const { data } = await api.post('/ksef/settings', settings);
  return data;
}

// Sprawdzenie statusu połączenia z KSEF
export async function checkKsefConnection(): Promise<KsefConnectionStatus> {
  try {
    const { data } = await api.get('/ksef/connection-status');
    return data;
  } catch (error: any) {
    return {
      connected: false,
      phase: 'disconnected',
      error: error.response?.data?.message || 'Błąd połączenia z KSEF'
    };
  }
}

// Status uwierzytelniania (token KSeF) + ewentualne pobranie tokenów dostępowych
export async function getKsefAuthStatus(): Promise<KsefConnectionStatus> {
  try {
    const { data } = await api.get('/ksef/auth/status');
    return data;
  } catch (error: any) {
    return {
      connected: false,
      phase: 'disconnected',
      error: error.response?.data?.message || 'Błąd pobierania statusu KSEF'
    };
  }
}

export async function listKsefInvoices(params: {
  subject: 'sales' | 'purchases';
  q?: string;
  month?: string; // YYYY-MM
  limit?: number;
  offset?: number;
}): Promise<KsefInvoiceListItem[]> {
  const { data } = await api.get('/ksef/invoices', { params });
  return data?.items || [];
}

export async function getKsefInvoice(ksefInvoiceId: string): Promise<KsefInvoiceDetails> {
  const { data } = await api.get(`/ksef/invoices/${encodeURIComponent(ksefInvoiceId)}`);
  // Backend zwraca { success: true, invoice: ..., items: ... }
  // Zwróć obiekt w formacie { invoice: ..., items: ... }
  if (data.success && data.invoice) {
    return {
      invoice: data.invoice,
      items: data.items || [],
      bankAccounts: data.bankAccounts || [],
      calcMode: data.calcMode,
      isNetto: data.isNetto,
      totalsFromItems: data.totalsFromItems,
      totalsMatch: data.totalsMatch,
    };
  }
  return {
    invoice: data.invoice || data,
    items: data.items || [],
    bankAccounts: data.bankAccounts || [],
    calcMode: data.calcMode,
    isNetto: data.isNetto,
    totalsFromItems: data.totalsFromItems,
    totalsMatch: data.totalsMatch,
  };
}

// Pobranie surowego XML z KSeF (do pobrania/debug)
export async function downloadKsefInvoiceXml(ksefInvoiceId: string): Promise<string> {
  const { data } = await api.get(`/ksef/invoices/${encodeURIComponent(ksefInvoiceId)}/xml`, {
    responseType: 'text' as any,
  });
  return String(data || '');
}

// Re-synchronizacja wszystkich faktur (ponowne parsowanie XML)
export async function resyncAllKsefInvoices(params?: {
  onlyMissing?: boolean;
  subjectType?: string;
  limit?: number;
  dryRun?: boolean;
}): Promise<{ success: boolean; total: number; ok: number; fail: number; skipped: number; failedIds: string[] }> {
  const { data } = await api.post('/ksef/invoices/resync-all', params || {});
  return data;
}

// Synchronizacja faktur z KSEF API
export async function syncKsefInvoices(params: {
  subject: 'sales' | 'purchases';
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
}): Promise<{ 
  success: boolean; 
  message: string; 
  synced: number; 
  errors: number; 
  total: number;
  cached?: boolean;
  cacheAgeMinutes?: number;
}> {
  const { data } = await api.post('/ksef/invoices/sync', params);
  return data;
}

// Uwierzytelnienie w KSEF (token)
export async function authenticateKsefToken(token: string, environment: 'demo' | 'production' = 'demo'): Promise<any> {
  const { data } = await api.post('/ksef/authenticate/token', { token, environment });
  return data;
}

// Uwierzytelnienie w KSEF (certyfikat)
export async function authenticateKsefCertificate(
  certificateFile: File,
  certificatePassword: string,
  environment: 'demo' | 'production' = 'demo'
): Promise<any> {
  const formData = new FormData();
  formData.append('certificate', certificateFile);
  formData.append('password', certificatePassword);
  formData.append('environment', environment);
  
  const { data } = await api.post('/ksef/authenticate/certificate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
}

